import { useState, useEffect, useRef } from "react";
import { io, Socket } from "socket.io-client";
import { notifications } from "@mantine/notifications";
import {
    TextInput,
    ActionIcon,
    Stack,
    Text,
    ScrollArea,
    Group,
    Avatar,
    Loader,
    Box,
    Button,
    Code,
    useMantineColorScheme,
} from "@mantine/core";
import { Send, Bot, User as UserIcon, Wand2, Sparkles } from "lucide-react";
import { useAuth } from "@bi-studio/shared/hooks";

interface Message {
    id: string;
    role: "user" | "assistant";
    content: string;
    query?: string;
    explanation?: string;
}

interface AiReportChatProps {
    dataSourceId?: string;
    initialQuery?: string;
    errorToFix?: string | null;
    onQueryApply: (query: string) => void;
}

export function AiReportChat({ dataSourceId, initialQuery, errorToFix, onQueryApply }: AiReportChatProps) {
    const { token } = useAuth();
    const { colorScheme } = useMantineColorScheme();
    const [socket, setSocket] = useState<Socket | null>(null);
    const [messages, setMessages] = useState<Message[]>([]);
    const [input, setInput] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [statusMessage, setStatusMessage] = useState<string | null>(null);
    const hasInitializedQuery = useRef(false);

    // Initialize with query if provided
    useEffect(() => {
        if (initialQuery && !hasInitializedQuery.current && messages.length === 0) {
            setMessages([
                {
                    id: "initial-query",
                    role: "assistant",
                    content: "Here is the current query for this report:",
                    query: initialQuery,
                },
            ]);
            hasInitializedQuery.current = true;
        }
    }, [initialQuery, messages.length]);

    // Auto-scroll to bottom
    const viewport = useRef<HTMLDivElement>(null);
    const scrollToBottom = () => {
        if (viewport.current) {
            viewport.current.scrollTo({ top: viewport.current.scrollHeight, behavior: 'smooth' });
        }
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages, statusMessage]);

    useEffect(() => {
        if (!token) return;

        // Initialize socket
        // Assume backend is on same host but different port/path or via proxy. 
        // For dev, backend is usually on 8082. Localhost might need explicit url.
        // In production this should be configured via env.
        const socketUrl = import.meta.env.VITE_API_URL || "http://localhost:8080";

        const newSocket = io(socketUrl, {
            auth: { token },
            transports: ["websocket"], // Force websocket to avoid polling issues often seen in dev
        });

        newSocket.on("connect", () => {
            console.log("Connected to AI Chat socket");
        });

        newSocket.on("status_update", (data: { status: string; message: string }) => {
            setStatusMessage(data.message);
        });

        newSocket.on("report_response", (data: { text: string; query?: string; explanation?: string }) => {
            setIsLoading(false);
            setStatusMessage(null);
            setMessages((prev) => [
                ...prev,
                {
                    id: Date.now().toString(),
                    role: "assistant",
                    content: data.text || data.explanation || "Done.",
                    query: data.query,
                    explanation: data.explanation,
                },
            ]);
        });

        newSocket.on("error", (data: { message: string }) => {
            setIsLoading(false);
            setStatusMessage(null);

            notifications.show({
                title: 'AI Assistant Error',
                message: data.message,
                color: 'red',
            });

            setMessages((prev) => [
                ...prev,
                {
                    id: Date.now().toString(),
                    role: "assistant",
                    content: `Error: ${data.message}`,
                },
            ]);
        });

        setSocket(newSocket);

        return () => {
            newSocket.disconnect();
        };
    }, [token]);

    // Safety timeout for frontend
    useEffect(() => {
        let timeoutId: NodeJS.Timeout;

        if (isLoading) {
            timeoutId = setTimeout(() => {
                setIsLoading(false);
                setStatusMessage(null);

                // Show timeout toast
                notifications.show({
                    title: 'Request Timed Out',
                    message: "The AI request took too long. Please try again.",
                    color: 'orange',
                });

                setMessages((prev) => [
                    ...prev,
                    {
                        id: Date.now().toString(),
                        role: "assistant",
                        content: "Error: Request timed out (Client Safety Timeout). Please try again.",
                    },
                ]);
            }, 70000); // 70 seconds (slightly longer than backend 60s timeout)
        }

        return () => {
            if (timeoutId) clearTimeout(timeoutId);
        };
    }, [isLoading]);

    const hasSentInitialContext = useRef(false);

    // Handle errorToFix
    useEffect(() => {
        if (errorToFix && socket && socket.connected) {
            const errorMsg: Message = {
                id: `error-${Date.now()}`,
                role: "user",
                content: `I'm getting this error: "${errorToFix}". Can you help me fix the query?`,
            };

            setMessages((prev) => [...prev, errorMsg]);
            setIsLoading(true);
            setStatusMessage("Analyzing error...");

            let promptToSend = errorMsg.content;
            if (initialQuery) {
                promptToSend = `Context - Current SQL Query associated with this report:\n\`\`\`sql\n${initialQuery}\n\`\`\`\n\nUser Request: ${errorMsg.content}`;
                hasSentInitialContext.current = true;
            }

            socket.emit("create_report_request", {
                prompt: promptToSend,
                dataSourceId,
            });
        }
    }, [errorToFix, socket]);

    const handleSend = () => {
        if (!input.trim() || !socket) return;

        const userMsg: Message = {
            id: Date.now().toString(),
            role: "user",
            content: input,
        };

        setMessages((prev) => [...prev, userMsg]);
        setInput("");
        setIsLoading(true);
        setStatusMessage("Sending request...");

        let promptToSend = input;

        // If we have an initial query and haven't sent it to the backend for this session yet,
        // prepend it to the prompt so the AI knows the context.
        if (initialQuery && !hasSentInitialContext.current) {
            promptToSend = `Context - Current SQL Query associated with this report:\n\`\`\`sql\n${initialQuery}\n\`\`\`\n\nUser Request: ${input}`;
            hasSentInitialContext.current = true;
        }

        socket.emit("create_report_request", {
            prompt: promptToSend,
            dataSourceId,
        });
    };

    return (
        <Box
            display="flex"
            style={{
                flexDirection: "column",
                height: 600,
                backgroundColor: colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.01)' : 'rgba(0, 0, 0, 0.01)',
                borderRadius: 'var(--mantine-radius-md)',
                border: '1px solid var(--modal-border)',
                overflow: 'hidden',
                position: 'relative'
            }}
        >
            <Box
                p="md"
                style={{
                    borderBottom: '1px solid var(--modal-border)',
                    backgroundColor: colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.02)',
                    backdropFilter: 'blur(5px)'
                }}
            >
                <Group>
                    <Wand2 size={20} />
                    <Text fw={600}>AI Assistant</Text>
                </Group>
            </Box>

            <ScrollArea flex={1} p="md" viewportRef={viewport}>
                <Stack>
                    {messages.length === 0 && (
                        <Text c="dimmed" fs="italic" ta="center" mt="xl">
                            Ask me to create a report for you. <br />
                            Example: "Show me total sales by month for 2024"
                        </Text>
                    )}

                    {messages.map((msg) => (
                        <Group key={msg.id} align="flex-start" justify={msg.role === "user" ? "flex-end" : "flex-start"}>
                            {msg.role === "assistant" && (
                                <Avatar color="dark" radius="xl" style={{ border: '1px solid var(--modal-border)' }}>
                                    <Bot size={20} />
                                </Avatar>
                            )}

                            <Box
                                p="sm"
                                style={{
                                    borderRadius: 'var(--mantine-radius-md)',
                                    backgroundColor: msg.role === 'user'
                                        ? (colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.05)')
                                        : (colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.04)' : 'rgba(255, 255, 255, 0.7)'),
                                    border: '1px solid var(--modal-border)',
                                    maxWidth: '80%',
                                    backdropFilter: msg.role === 'assistant' ? 'blur(10px)' : undefined,
                                    boxShadow: 'var(--mantine-shadow-xs)'
                                }}
                            >
                                <Text size="sm">{msg.content}</Text>
                                {msg.query && (
                                    <Stack mt="xs">
                                        <Code
                                            block
                                            style={{
                                                backgroundColor: colorScheme === 'dark' ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 0, 0, 0.05)',
                                                border: '1px solid var(--modal-border)'
                                            }}
                                        >
                                            {msg.query}
                                        </Code>
                                        <Button
                                            size="xs"
                                            variant="light"
                                            color="gray"
                                            onClick={() => onQueryApply(msg.query!)}
                                            leftSection={<Sparkles size={14} />}
                                            style={{
                                                backgroundColor: colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
                                            }}
                                        >
                                            Apply Query
                                        </Button>
                                    </Stack>
                                )}
                            </Box>

                            {msg.role === "user" && (
                                <Avatar radius="xl" color="gray" style={{ border: '1px solid var(--modal-border)' }}>
                                    <UserIcon size={20} />
                                </Avatar>
                            )}
                        </Group>
                    ))}

                    {isLoading && (
                        <Group>
                            <Avatar color="dark" radius="xl" style={{ border: '1px solid var(--modal-border)' }}>
                                <Bot size={20} />
                            </Avatar>
                            <Box
                                p="sm"
                                style={{
                                    borderRadius: 'var(--mantine-radius-md)',
                                    backgroundColor: colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.04)' : 'rgba(255, 255, 255, 0.7)',
                                    border: '1px solid var(--modal-border)',
                                    backdropFilter: 'blur(10px)'
                                }}
                            >
                                <Group gap="xs">
                                    <Loader size="xs" color="gray" />
                                    <Text size="sm" c="dimmed">
                                        {statusMessage || "Thinking..."}
                                    </Text>
                                </Group>
                            </Box>
                        </Group>
                    )}
                </Stack>
            </ScrollArea>

            <Box
                p="md"
                style={{
                    borderTop: '1px solid var(--modal-border)',
                    backgroundColor: colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.01)',
                    backdropFilter: 'blur(5px)'
                }}
            >
                <Group>
                    <TextInput
                        placeholder={dataSourceId ? "Describe your report..." : "Select a data source first..."}
                        style={{ flex: 1 }}
                        value={input}
                        onChange={(e) => setInput(e.currentTarget.value)}
                        onKeyDown={(e) => e.key === "Enter" && handleSend()}
                        disabled={isLoading || !dataSourceId}
                        variant="filled"
                        styles={{
                            input: {
                                backgroundColor: colorScheme === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(255, 255, 255, 0.5)',
                                borderColor: 'var(--modal-border)',
                            }
                        }}
                        rightSection={
                            <ActionIcon
                                onClick={handleSend}
                                disabled={isLoading || !input.trim() || !dataSourceId}
                                variant="subtle"
                                color="gray"
                            >
                                <Send size={18} />
                            </ActionIcon>
                        }
                    />
                </Group>
            </Box>
        </Box>
    );
}
